import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState } from "../ui";
import { blobErrorDetail, saveBlob } from "./admin-files/shared";
import { UploadForm } from "./admin-files/UploadForm";
import { FileRow } from "./admin-files/FileRow";
import { ReceiptConfirmModal } from "./admin-files/ReceiptConfirmModal";

const resendToast = (es) => {
  if (!es) return toast.success("Arquivo reenviado.");
  if (es.sent > 0)
    return toast.success(
      `Reenviado: e-mail entregue ao SMTP para ${es.delivered_to.join(", ")}` +
        (es.failed > 0 ? ` · ${es.failed} falha(s)` : "")
    );
  if (es.skipped_no_email > 0)
    return toast.warning("Nenhum destinatário possui e-mail cadastrado. Cadastre um e-mail primeiro.");
  if (es.failed > 0) return toast.warning("O reenvio falhou no SMTP. Verifique a configuração de e-mail.");
  return toast.info("Nenhum e-mail enviado.");
};

const HeaderActions = ({ onToggle }) => (
  <div className="flex flex-wrap gap-2">
    <Link
      data-testid="lote-link"
      to="/admin/arquivos/boletos-lote"
      className="inline-flex items-center gap-2 border border-slate-300 text-slate-800 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
    >
      <UploadCloud size={14} /> Boletos em lote
    </Link>
    <button
      data-testid="upload-btn"
      onClick={onToggle}
      className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
    >
      <Plus size={14} /> Enviar arquivo
    </button>
  </div>
);

// Row-level actions (download, resend, receipt, delete) for the files list.
function useFileActions(load) {
  const [resendingId, setResendingId] = useState(null);
  const [issuingReceiptId, setIssuingReceiptId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Excluir este arquivo? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/admin/files/${id}`);
      toast.success("Arquivo excluído");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const issueReceipt = async (f) => {
    setIssuingReceiptId(f.id);
    try {
      const res = await api.post(`/admin/files/${f.id}/receipt`, {}, { responseType: "blob" });
      const match = (res.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `Recibo-${f.numero_boleto || f.id}.pdf`;
      saveBlob(res.data, filename, "application/pdf");
      toast.success(`Recibo emitido: ${filename}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(await blobErrorDetail(e, "Falha ao emitir recibo.")));
    } finally {
      setIssuingReceiptId(null);
    }
  };

  const resend = async (f) => {
    setResendingId(f.id);
    try {
      const res = await api.post(`/admin/files/${f.id}/resend`);
      resendToast(res.data?.email_status);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao reenviar.");
    } finally {
      setResendingId(null);
    }
  };

  const download = async (f) => {
    if (f.available === false) {
      toast.error("Arquivo físico não está mais disponível no servidor. Peça ao administrador para reenviar o documento.");
      return;
    }
    try {
      const res = await api.get(`/portal/files/${f.id}/download`, { responseType: "blob" });
      saveBlob(res.data, f.file_name);
    } catch (err) {
      toast.error(await blobErrorDetail(err, "Erro ao baixar."));
    }
  };

  return { resendingId, issuingReceiptId, remove, issueReceipt, resend, download };
}

export default function AdminFiles() {
  const [files, setFiles] = useState(null);
  const [associados, setAssociados] = useState([]);
  const [open, setOpen] = useState(false);
  const [receiptConfirm, setReceiptConfirm] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/admin/files")
        .then((r) => setFiles(r.data))
        .catch((e) => {
          setFiles([]);
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }),
    []
  );
  const actions = useFileActions(load);

  useEffect(() => {
    load();
    api
      .get("/admin/associados")
      .then((r) => setAssociados(r.data.filter((u) => u.status === "ativo")))
      .catch((e) => console.warn("Falha ao carregar associados:", e));
  }, [load]);

  return (
    <div data-testid="admin-files">
      <PageHeader
        title="Arquivos & Boletos"
        subtitle="Envie boletos, comunicados, contratos e demais documentos aos associados."
        actions={<HeaderActions onToggle={() => setOpen((v) => !v)} />}
      />
      {open && (
        <UploadForm
          associados={associados}
          onCancel={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            load();
          }}
        />
      )}
      {files === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : files.length === 0 ? (
        <EmptyState title="Nenhum arquivo enviado ainda" hint="Use o botão acima para enviar boletos e documentos." />
      ) : (
        <Card>
          <ul className="divide-y divide-slate-200">
            {files.map((f) => (
              <FileRow
                key={f.id}
                f={f}
                resendingId={actions.resendingId}
                issuingReceiptId={actions.issuingReceiptId}
                onDownload={actions.download}
                onResend={actions.resend}
                onIssue={setReceiptConfirm}
                onRemove={actions.remove}
              />
            ))}
          </ul>
        </Card>
      )}
      {receiptConfirm && (
        <ReceiptConfirmModal
          receiptConfirm={receiptConfirm}
          associados={associados}
          onCancel={() => setReceiptConfirm(null)}
          onConfirm={(f) => {
            setReceiptConfirm(null);
            actions.issueReceipt(f);
          }}
        />
      )}
    </div>
  );
}
